// App Insight interne (CCT-193, Phase 2) — identifiant de SESSION front (app).
//
// Fournit un `getSessionId()` SYNCHRONE (le wrapper HTTP en a besoin pour poser le
// header à chaque requête). Deux transports selon la plateforme :
//   - RN Web : localStorage (synchrone) → id stable entre rechargements.
//   - Natif  : id en mémoire pour la durée du lancement ; persistance best-effort
//              dans AsyncStorage via `hydrateSessionId()` (appelée au bootstrap).
//
// Best-effort : jamais d'exception propagée.

import AsyncStorage from '@react-native-async-storage/async-storage';

// ---------------------------------------------------------------------------
// OÙ VIT L'IDENTITÉ DE SESSION — PAR ONGLET (CCT-263)
// ---------------------------------------------------------------------------
// Elle vivait dans `localStorage`, donc PARTAGÉE PAR TOUS LES ONGLETS. Deux onglets ouverts
// sur le produit se retrouvaient dans la MÊME session, chacun avançant le compteur de rangs
// de son côté : mesuré sur une session réelle, les rangs 16 à 21 portés chacun par DEUX
// évènements distincts, à 400 ms d'écart. `parent=18` ne désignait alors plus rien de précis
// et la timeline pouvait rattacher un appel à une action d'un autre onglet.
//
// Coordonner deux onglets sur un compteur partagé est possible mais fragile — lecture,
// modification, écriture, sans verrou. On supprime le problème au lieu de l'arbitrer :
// `sessionStorage` est PROPRE À L'ONGLET et SURVIT AU RECHARGEMENT, exactement la durée de vie
// qu'on veut donner à une session. Deux onglets = deux sessions, ce qui est d'ailleurs ce
// qu'ils sont.
//
// Repli sur `localStorage` : hors navigateur (React Native), `sessionStorage` n'existe pas —
// et il n'y a alors qu'un seul context, donc aucun partage à craindre.
function magasin(): Storage | null {
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage) return sessionStorage;
  } catch {
    /* accès refusé (cookies bloqués) : on tente le suivant */
  }
  try {
    // ⚠️ Test ÉCRIT EN DUR, pas via `magasin()` : c'est la fonction elle-même. Une
    // substitution globale l'avait rewrité en appel récursif — la lecture de session partait
    // en boucle et chaque chargement mintait un identifiant neuf.
    if (typeof localStorage !== 'undefined' && localStorage) return localStorage;
  } catch {
    /* rien de persistant : l'identité vivra en mémoire pour la durée de la page */
  }
  return null;
}


const LS_SID = 'cc_insight_sid';
// Dernière activité connue, pour faire EXPIRER la session (CCT-249).
const LS_SEEN = 'cc_insight_seen';

/** Inactivité au-delà de laquelle l'id est renouvelé. 30 min = la convention des outils
 *  d'analyse d'audience ; assez long pour survivre à une pause, assez court pour qu'une
 *  reprise le lendemain soit une AUTRE session. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;

// L'horodatage n'est réécrit qu'au-delà de ce pas : `getSessionId()` est appelé à CHAQUE
// requête HTTP, et une écriture localStorage par appel serait du gaspillage pur.
const SEEN_WRITE_STEP_MS = 30 * 1000;

let _sid: string | null = null;
let _lastWrite = 0;

function genId(): string {
  try {
    const c: Crypto | undefined =
      typeof crypto !== 'undefined' ? (crypto as Crypto) : undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch {
    /* ignore */
  }
  // Repli natif (pas de crypto.randomUUID) : suffisant pour une corrélation.
  return `sid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Force un identifiant NEUF, immédiatement. Renvoie le nouvel id.
 *
 * CONTREPARTIE INDISPENSABLE de l'effacement définitif (CCT-249). Le back-office peut vider
 * sa liste, mais il n'a aucun moyen de dire au navigateur de repartir à zéro : l'utilisateur
 * effaçait, continuait à naviguer, et son activité restait rattachée à une session ensevelie
 * — donc invisible. Sans ce geste, « effacer puis tester » est impossible autrement qu'en
 * vidant le stockage du navigateur ou en attendant 30 minutes.
 *
 * Exposé sur `window.ccNewSession()` (web) pour être déclenchable depuis la console, et
 * appelé automatiquement quand l'URL porte `?ccNewSession=1`.
 */
export function newSessionId(): string {
  const id = genId();
  _sid = id;
  _seq = 0;
  _lastWrite = Date.now();
  try {
    if (magasin() !== null) {
      magasin()?.setItem(LS_SID, id);
      magasin()?.setItem(LS_SEEN, String(_lastWrite));
      // Le compteur appartient À LA SESSION : une session neuve repart de zéro, sinon les
      // rangs continueraient de croître d'une session à l'autre sans jamais se recaler.
      magasin()?.setItem(LS_SEQ, '0');
      _lastAction = 0;   // une session neuve n'hérite d'aucun parent
      magasin()?.setItem(LS_ACTION, '0');
    }
  } catch {
    /* best-effort : l'id vit au moins en mémoire pour la durée de la page */
  }
  return id;
}

/** Vrai si la dernière activité connue est trop ancienne pour prolonger la session. */
export function isExpired(lastSeen: number | null, now: number): boolean {
  if (!lastSeen || !Number.isFinite(lastSeen)) return false;   // inconnu -> on prolonge
  if (lastSeen > now) return false;                            // horloge reculée -> on prolonge
  return now - lastSeen > SESSION_IDLE_MS;
}

/** Id de session courant, créé/persisté au premier appel (synchrone).
 *
 * L'id EXPIRE après `SESSION_IDLE_MS` d'inactivité (CCT-249). Il était auparavant écrit une
 * fois dans `localStorage` et gardé À VIE : le même navigateur renvoyait éternellement le
 * même identifiant, et l'écran Sessions du BO ne montrait qu'UNE ligne par poste, grossissant
 * sans fin. Conséquence directe et déroutante : après un « Tout effacer », revenir naviguer
 * ne créait AUCUNE nouvelle session — l'activité s'ajoutait à la ligne effacée. Une session
 * doit finir pour qu'une autre puisse commencer.
 */
export function getSessionId(): string {
  const now = Date.now();
  // RN Web : localStorage synchrone.
  try {
    if (magasin() !== null) {
      const existing = magasin()?.getItem(LS_SID);
      const seen = Number(magasin()?.getItem(LS_SEEN) || '') || null;
      if (existing && !isExpired(seen, now)) {
        _sid = existing;
        // Écriture ESPACÉE : cette fonction est appelée à chaque requête HTTP.
        if (now - _lastWrite > SEEN_WRITE_STEP_MS) {
          magasin()?.setItem(LS_SEEN, String(now));
          _lastWrite = now;
        }
        return existing;
      }
      // Session expirée ou inexistante : on passe par `newSessionId`, qui remet AUSSI le
      // compteur à zéro. Dupliquer la création ici laissait le compteur de l'ancienne
      // session courir sur la nouvelle — les rangs auraient continué de croître sans
      // jamais se recaler, et deux sessions successives auraient été indiscernables.
      return newSessionId();
    }
  } catch {
    /* ignore */
  }
  if (_sid) return _sid;
  // Natif : id en mémoire (stable pour la durée du lancement).
  _sid = genId();
  return _sid;
}

/**
 * Persistance NATIVE best-effort (AsyncStorage est asynchrone). À appeler au
 * bootstrap : si un id est déjà stocké et qu'aucun n'a encore été distribué, on
 * l'adopte (id stable entre lancements) ; sinon on persiste l'id courant.
 * No-op sur le web (déjà géré par localStorage de façon synchrone).
 */
export async function hydrateSessionId(): Promise<void> {
  try {
    if (magasin() !== null) {
      // WEB — deux accès au renouvellement, tous deux destinés au diagnostic :
      //   * `?ccNewSession=1` dans l'URL — utilisable sans ouvrir la console ;
      //   * `window.ccNewSession()` — depuis la console, sans recharger la page.
      // Le paramètre est retiré de l'URL après coup, sinon chaque rechargement (F5) créerait
      // une session de plus et fragmenterait la timeline qu'on cherche justement à lire.
      try {
        const w = window as any;
        w.ccNewSession = newSessionId;
        const url = new URL(window.location.href);
        if (url.searchParams.get('ccNewSession')) {
          newSessionId();
          url.searchParams.delete('ccNewSession');
          window.history.replaceState({}, '', url.toString());
        }
      } catch {
        /* best-effort */
      }
      return; // web : rien d'autre à faire, localStorage est synchrone
    }
    const stored = await AsyncStorage.getItem(LS_SID);
    if (stored && !_sid) {
      _sid = stored;
      return;
    }
    await AsyncStorage.setItem(LS_SID, getSessionId());
  } catch {
    /* best-effort */
  }
}

// ---------------------------------------------------------------------------
// NUMÉRO DE SÉQUENCE — l'ORDRE dans la session (CCT-240)
// ---------------------------------------------------------------------------
// POURQUOI L'HORODATAGE NE SUFFIT PAS. La timeline d'une session mêle des événements
// produits par DEUX horloges — celle du navigateur et celle du serveur — qui ne sont ni
// synchronisées ni comparables à la milliseconde. Et l'horodatage d'un span est son
// DÉBUT, pas sa fin : un appel client de 400 ms « commence » avant le span serveur qu'il
// déclenche, mais se termine après. Aucun tri par date ne peut donc restituer l'ordre
// causal — c'est ce qui rendait la timeline illisible.
//
// LE COUPLE RETENU : (`cc.session_id`, `cc.session_seq`). L'identifiant de session seul ne
// suffit pas — il dit DANS QUELLE session, pas DANS QUEL ORDRE. Avec ce compteur, il donne
// un ordre TOTAL et déterministe, indépendant de toute horloge.
//
// PERSISTÉ AVEC LA SESSION (CCT-253). Il ne l'était pas : il repartait à 1 à CHAQUE
// chargement de page, alors que l'identifiant de session, lui, survit dans `localStorage`.
// Une session couvrant plusieurs chargements, le même rang était donc attribué plusieurs
// fois — la clé censée donner un ordre TOTAL collisionnait.
//
// Ce n'était pas un cas rare : le flux de connexion fait un `window.location.replace`, donc
// toute connexion en cours de session garantissait la remise à zéro. MESURÉ sur une session
// réelle : `seq=1` sur deux appels distincts, `seq=2`, `3`, `4` et `11` chacun porté par un
// événement client ET un événement serveur.
//
// La crainte d'origine (« deux onglets partagent l'id de session mais pas ce module »)
// s'inverse une fois le compteur partagé : deux onglets incrémentent la MÊME valeur, ce qui
// produit précisément l'ordre total recherché. Il reste une course possible entre deux
// onglets qui lisent la même valeur au même instant — un doublon occasionnel, sans commune
// mesure avec une remise à zéro systématique à chaque rechargement.
const LS_SEQ = 'cc_insight_seq';

let _seq = 0;

/** Numéro de séquence SUIVANT. Monotone sur toute la durée de la SESSION, pas de la page. */
export function nextSeq(): number {
  try {
    if (magasin() !== null) {
      const n = (Number(magasin()?.getItem(LS_SEQ) || '') || 0) + 1;
      magasin()?.setItem(LS_SEQ, String(n));
      _seq = n;
      return n;
    }
  } catch {
    /* stockage indisponible : on retombe sur le compteur mémoire */
  }
  _seq += 1;
  return _seq;
}

// ---------------------------------------------------------------------------
// PARENT — l'action CLIENTE qui a causé un appel (CCT-253)
// ---------------------------------------------------------------------------
// POURQUOI `seq` NE SUFFIT PAS. Il donne l'ordre d'ÉMISSION, pas la CAUSALITÉ. Un appel
// déclenché par le clic A peut partir — ou retomber — APRÈS le clic B : dans la suite plate,
// il se range derrière B et paraît causé par lui. Sur un écran qui sert à comprendre « ce que
// ce clic a provoqué », c'est une réponse fausse, pas une réponse imprécise.
//
// Le pointeur transforme la suite en FORÊT : chaque appel désigne l'action cliente qui l'a
// causé, et la timeline peut le ranger sous elle quel que soit son instant réel.
//
// DEUX PIÈGES, traités ici :
//   1. le parent est la dernière ACTION CLIENTE, pas le dernier `seq`. Le compteur est
//      partagé avec les appels API : prendre « le rang précédent » ferait pointer un appel
//      vers un AUTRE appel, et la chaîne remonterait à un ancêtre arbitraire au lieu du clic.
//   2. les appels de FOND (polling des notifications, renouvellement de session) ne sont
//      causés par aucun clic. Leur donner le dernier clic en cours attribuerait à
//      l'utilisateur une intention qu'il n'a pas eue — d'où `parentSeq(background)`.
//   3. il doit SURVIVRE AU RECHARGEMENT, comme la séquence. Il ne l'était pas : la séquence
//      était persistée mais pas lui. Après une connexion (`window.location.replace`), tous
//      les appels de bootstrap repartaient donc SANS parent. MESURÉ sur une session réelle :
//      huit appels d'affilée orphelins alors que la navigation qui les avait causés figurait
//      juste au-dessus dans la timeline.
const LS_ACTION = 'cc_insight_action';

let _lastAction = 0;

function readAction(): number {
  if (_lastAction > 0) return _lastAction;
  try {
    if (magasin() !== null) {
      _lastAction = Number(magasin()?.getItem(LS_ACTION) || '') || 0;
    }
  } catch {
    /* stockage indisponible : on reste sur la mémoire */
  }
  return _lastAction;
}

/** Déclare une ACTION CLIENTE (navigation, clic) : elle devient le parent des appels suivants. */
export function markClientAction(seq: number): void {
  if (!Number.isFinite(seq) || seq <= 0) return;
  _lastAction = seq;
  try {
    if (magasin() !== null) magasin()?.setItem(LS_ACTION, String(seq));
  } catch {
    /* best-effort */
  }
}

/** Parent à annoncer sur un appel. `null` pour une requête de FOND — sans cause utilisateur. */
export function parentSeq(withoutCause = false): number | null {
  // ⚠️ Le paramètre s'appelait `background` et recevait le drapeau du même nom (CCT-259).
  // Or `background` sert D'ABORD à exclure un appel de l'indicateur de chargement global
  // (CCT-177) : deux préoccupations sans rapport, confondues tant que l'appel concerné était
  // un poll. Le poll des notifications supprimé (CCT-233), l'appel a désormais une vraie
  // cause — un clic sur « rafraîchir », un retour d'onglet — mais restait marqué sans cause
  // parce qu'il ne devait pas afficher de spinner.
  //
  // Ici, la question est UNIQUEMENT : cet appel a-t-il une action cliente à l'origine ?
  if (withoutCause) return null;
  const a = readAction();
  return a > 0 ? a : null;
}

/** Dernier numéro distribué (diagnostic et tests). */
export function currentSeq(): number {
  return _seq;
}
